import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import satelliteApi from '../services/satelliteApi';
import StreetViewPanel from './StreetViewPanel';
import './SatelliteMap.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Available map layers with terrain and vegetation options
const MAP_LAYERS = {
  satellite: {
    name: 'Satellite Imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>'
  },
  terrain: {
    name: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
  },
  vegetation: {
    name: 'Vegetation (NDVI)',
    url: 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_NDVI_8Day/default/2024-01-01/GoogleMapsCompatible_Level9/{z}/{y}/{x}.png',
    attribution: '&copy; NASA GIBS'
  },
  streets: {
    name: 'Street Map',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  },
  topo: {
    name: 'Topographic',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri, DeLorme, NAVTEQ'
  },
  dark: {
    name: 'Dark Mode',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
};

// Map center updater component
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && 
        Array.isArray(center) && 
        center.length >= 2 &&
        typeof center[0] === 'number' && 
        typeof center[1] === 'number' &&
        !isNaN(center[0]) && 
        !isNaN(center[1])) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);
  return null;
};

const SatelliteMap = ({ fullscreen = false, compact = false }) => {
  const { events, selectedEvent, setSelectedEvent, selectedDisasterType, mapView, setMapView } = useApp();
  const { user } = useAuth();
  const lowDataMode = user?.preferences?.lowDataMode || false;
  const [activeFires, setActiveFires] = useState([]);
  const [fireDataSource, setFireDataSource] = useState(null);
  const [showFires, setShowFires] = useState(true);
  const [loadingFires, setLoadingFires] = useState(false);
  const [activeLayer, setActiveLayer] = useState('satellite');
  
  // Street View state
  const [showStreetView, setShowStreetView] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState(null);
  
  // Location search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedLocation, setSearchedLocation] = useState(null);
  const [mapZoom, setMapZoom] = useState(10);

  // Search for location using Nominatim (OpenStreetMap)
  const searchLocation = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching location:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  // Handle location selection
  const selectLocation = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSearchedLocation({ lat, lng, name: result.display_name });
    setMapZoom(13);
    setSearchResults([]);
    setSearchQuery(result.display_name.split(',')[0]);
  };

  // Handle search on Enter key
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      searchLocation();
    }
  };

  // Fetch active fires when map center changes
  useEffect(() => {
    if (lowDataMode) {
      setActiveFires([]);
      setFireDataSource(null);
      return;
    }
    const fetchFires = async () => {
      const lat = selectedEvent?.location?.coordinates?.[1] || -26.2041;
      const lng = selectedEvent?.location?.coordinates?.[0] || 28.0473;
      
      setLoadingFires(true);
      try {
        const response = await satelliteApi.getFiresNearby(lat, lng, 200, 7);
        if (response.fires) {
          setActiveFires(response.fires);
          setFireDataSource(response.dataSource);
          console.log(`Loaded ${response.fires.length} fires from ${response.dataSource}`);
        }
      } catch (err) {
        console.error('Error fetching fire data:', err);
        // Try to get general fire data as fallback
        try {
          const fallbackResponse = await satelliteApi.getFireData('VIIRS_NOAA20_NRT', 7);
          if (fallbackResponse.fires) {
            // Filter fires within rough bounding box
            const filteredFires = fallbackResponse.fires.filter(fire => {
              const fireLat = fire.latitude;
              const fireLng = fire.longitude;
              return Math.abs(fireLat - lat) < 2 && Math.abs(fireLng - lng) < 2;
            });
            setActiveFires(filteredFires);
            setFireDataSource(fallbackResponse.dataSource);
          }
        } catch (fallbackErr) {
          console.error('Fallback fire fetch failed:', fallbackErr);
          setActiveFires([]);
        }
      } finally {
        setLoadingFires(false);
      }
    };

    fetchFires();
  }, [selectedEvent, lowDataMode]);

  const getDisasterIcon = (type) => {
    const icons = {
      wildfire: '🔥',
      flood: '🌊',
      drought: '🌵',
      windstorm: '💨'
    };
    return icons[type] || '📍';
  };

  // Disaster-type based marker colors (distinct per type)
  const getMarkerColor = (event) => {
    const disasterColors = {
      wildfire: '#e53e3e',   // Red
      flood: '#3182ce',      // Blue
      drought: '#dd6b20',    // Orange
      windstorm: '#805ad5'   // Purple
    };
    return disasterColors[event.disasterType] || '#38a169';
  };

  const getMarkerBorderColor = (event) => {
    const borderColors = {
      wildfire: '#c53030',
      flood: '#2b6cb0',
      drought: '#c05621',
      windstorm: '#6b46c1'
    };
    return borderColors[event.disasterType] || '#2f855a';
  };

  // Safe toFixed helper
  const safeToFixed = (value, decimals = 1, fallback = '--') => {
    if (value === null || value === undefined || value === '--' || value === '') return fallback;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return fallback;
    return num.toFixed(decimals);
  };

  const getDefaultCenter = () => {
    if (selectedEvent?.location?.coordinates &&
        Array.isArray(selectedEvent.location.coordinates) &&
        selectedEvent.location.coordinates.length >= 2 &&
        typeof selectedEvent.location.coordinates[0] === 'number' &&
        typeof selectedEvent.location.coordinates[1] === 'number' &&
        !isNaN(selectedEvent.location.coordinates[0]) &&
        !isNaN(selectedEvent.location.coordinates[1])) {
      return [selectedEvent.location.coordinates[1], selectedEvent.location.coordinates[0]];
    }
    return [-26.2041, 28.0473]; // Default to Johannesburg
  };

  const defaultCenter = getDefaultCenter();
  
  // Determine map center - prioritize searched location
  const mapCenter = searchedLocation 
    ? [searchedLocation.lat, searchedLocation.lng] 
    : defaultCenter;

  // Open Street View for current location
  const openStreetView = (lat, lng) => {
    setStreetViewLocation({ lat, lng });
    setShowStreetView(true);
  };

  // Close Street View
  const closeStreetView = () => {
    setShowStreetView(false);
    setStreetViewLocation(null);
  };

  // If Street View is open, show that instead
  if (showStreetView) {
    return (
      <div className={`satellite-map ${fullscreen ? 'fullscreen' : ''} ${compact ? 'compact' : ''}`}>
        <StreetViewPanel 
          lat={streetViewLocation?.lat || mapCenter[0]}
          lng={streetViewLocation?.lng || mapCenter[1]}
          onClose={closeStreetView}
          compact={compact}
        />
      </div>
    );
  }

  return (
    <div className={`satellite-map ${fullscreen ? 'fullscreen' : ''} ${compact ? 'compact' : ''}`}>
      <div className="map-header">
        <div className="map-title">
          <span className="map-icon">🌍</span>
          <h3>Sentinel-2 Satellite Analysis</h3>
        </div>
        
        {/* Street View Toggle */}
        <button 
          className="street-view-toggle"
          onClick={() => openStreetView(mapCenter[0], mapCenter[1])}
          title="Open Street View 360°"
        >
          🏠 Street View
        </button>
        
        {/* Location Search */}
        <div className="location-search">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="search-input"
            />
            <button 
              className="search-btn"
              onClick={searchLocation}
              disabled={isSearching}
            >
              {isSearching ? '⏳' : '🔍'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((result, idx) => (
                <div 
                  key={idx}
                  className="search-result-item"
                  onClick={() => selectLocation(result)}
                >
                  <span className="result-icon">📍</span>
                  <span className="result-text">{result.display_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="map-view-toggle">
          <select 
            value={activeLayer} 
            onChange={(e) => setActiveLayer(e.target.value)}
            className="layer-select"
          >
            {Object.entries(MAP_LAYERS).map(([key, layer]) => (
              <option key={key} value={key}>{layer.name}</option>
            ))}
          </select>
          {/* Only show fire toggle when viewing wildfire or all disasters */}
          {(selectedDisasterType === 'all' || selectedDisasterType === 'wildfire') && (
            <button 
              className={`view-btn fire-btn ${showFires ? 'active' : ''}`}
              onClick={() => setShowFires(!showFires)}
              title="Toggle NASA FIRMS fire data"
            >
              🔥 {loadingFires ? '...' : activeFires.length}
            </button>
          )}
        </div>
      </div>

      <div className="map-monitoring-badge">
        <span className="monitoring-icon">{getDisasterIcon(selectedDisasterType)}</span>
        <span className="monitoring-label">{selectedDisasterType?.toUpperCase() || 'DISASTER'} MONITORING</span>
        {selectedEvent?.satelliteData?.ndviCurrent && (
          <span className="ndvi-badge">
            NDVI: {safeToFixed(selectedEvent.satelliteData.ndviCurrent, 2)}
          </span>
        )}
        {searchedLocation && (
          <span className="location-badge">
            📍 {searchedLocation.name.split(',')[0]}
          </span>
        )}
      </div>

      <div className="map-container">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <MapUpdater center={mapCenter} zoom={mapZoom} />
          <LayersControl position="topright">
            {Object.entries(MAP_LAYERS).map(([key, layer]) => (
              <LayersControl.BaseLayer 
                key={key} 
                checked={activeLayer === key} 
                name={layer.name}
              >
                <TileLayer url={layer.url} attribution={layer.attribution} />
              </LayersControl.BaseLayer>
            ))}
          </LayersControl>
          
          {/* Default layer */}
          <TileLayer
            url={MAP_LAYERS[activeLayer].url}
            attribution={MAP_LAYERS[activeLayer].attribution}
          />
          
          {/* Searched Location Marker */}
          {searchedLocation && (
            <Marker position={[searchedLocation.lat, searchedLocation.lng]}>
              <Popup>
                <div className="searched-location-popup">
                  <h4>📍 {searchedLocation.name.split(',')[0]}</h4>
                  <p>{searchedLocation.name}</p>
                  <p className="coords">
                    {searchedLocation.lat.toFixed(4)}°, {searchedLocation.lng.toFixed(4)}°
                  </p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {events
            .filter(event => 
              event.location?.coordinates && 
              Array.isArray(event.location.coordinates) &&
              event.location.coordinates.length >= 2 &&
              typeof event.location.coordinates[0] === 'number' &&
              typeof event.location.coordinates[1] === 'number' &&
              !isNaN(event.location.coordinates[0]) &&
              !isNaN(event.location.coordinates[1]) &&
              // Filter by selected disaster type
              (selectedDisasterType === 'all' || event.disasterType === selectedDisasterType)
            )
            .map((event) => (
              <CircleMarker
                key={event._id}
                center={[event.location.coordinates[1], event.location.coordinates[0]]}
                radius={selectedEvent?._id === event._id ? 15 : 10}
                pathOptions={{
                  color: getMarkerBorderColor(event),
                  fillColor: getMarkerColor(event),
                  fillOpacity: selectedEvent?._id === event._id ? 0.8 : 0.6,
                  weight: selectedEvent?._id === event._id ? 3 : 2
                }}
                eventHandlers={{
                  click: () => setSelectedEvent(event)
                }}
              >
                <Popup>
                  <div className="marker-popup">
                    <strong>{getDisasterIcon(event.disasterType)} {event.erfNumber || 'Event'}</strong>
                    <p style={{color: getMarkerColor(event), fontWeight: 'bold', textTransform: 'capitalize'}}>
                      {event.disasterType || 'Unknown'} Event
                    </p>
                    <p>{event.commodity?.name || 'Unknown'} - {event.lossPercentage || 0}% loss</p>
                    <p>R {(event.estimatedLoss || 0).toLocaleString()} estimated loss</p>
                    <button 
                      className="popup-street-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openStreetView(event.location.coordinates[1], event.location.coordinates[0]);
                      }}
                    >
                      🏠 View in Street View 360°
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            ))}

          {/* NASA FIRMS Active Fire Markers - only show for wildfire or 'all' disaster type */}
          {showFires && (selectedDisasterType === 'all' || selectedDisasterType === 'wildfire') && activeFires
            .filter(fire => 
              fire.latitude != null && 
              fire.longitude != null &&
              typeof fire.latitude === 'number' &&
              typeof fire.longitude === 'number' &&
              !isNaN(fire.latitude) &&
              !isNaN(fire.longitude)
            )
            .map((fire, idx) => (
              <CircleMarker
                key={`fire-${idx}`}
                center={[fire.latitude, fire.longitude]}
                radius={Math.min(fire.frp ? fire.frp / 10 + 5 : 6, 15)}
                pathOptions={{
                  color: '#ff4500',
                  fillColor: fire.brightness > 330 ? '#ff0000' : '#ff6600',
                  fillOpacity: 0.7,
                  weight: 2
                }}
              >
                <Popup>
                  <div className="fire-popup">
                    <strong>🔥 Active Fire Detection</strong>
                    <p><b>Satellite:</b> {fire.satellite || 'VIIRS'}</p>
                    <p><b>Instrument:</b> {fire.instrument || 'VIIRS'}</p>
                    <p><b>Brightness:</b> {safeToFixed(fire.brightness, 1)}K</p>
                    <p><b>FRP:</b> {safeToFixed(fire.frp, 1)} MW</p>
                    <p><b>Confidence:</b> {fire.confidence === 'h' ? 'High' : fire.confidence === 'n' ? 'Nominal' : fire.confidence === 'l' ? 'Low' : fire.confidence}</p>
                    <p><b>Date:</b> {fire.acqDate}</p>
                    <p><b>Time:</b> {fire.acqTime ? `${fire.acqTime.substring(0,2)}:${fire.acqTime.substring(2)} UTC` : 'N/A'}</p>
                    <p><b>Day/Night:</b> {fire.dayNight === 'D' ? '☀️ Day' : '🌙 Night'}</p>
                    <small style={{color: '#666'}}>Source: NASA FIRMS ({fireDataSource || 'Local Data'})</small>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
        </MapContainer>
      </div>

      <div className="map-legend">
        <h4>Disaster Types</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{background: '#e53e3e'}}></span>
            <span>🔥 Wildfire</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: '#3182ce'}}></span>
            <span>🌊 Flood</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: '#dd6b20'}}></span>
            <span>🌵 Drought</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: '#805ad5'}}></span>
            <span>💨 Windstorm</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{background: '#ff4500'}}></span>
            <span>🔥 NASA FIRMS Fires</span>
          </div>
        </div>
        <div className="map-layer-info">
          <span>📍 Layer: {MAP_LAYERS[activeLayer].name}</span>
        </div>
      </div>

      <div className="map-info">
        <div className="map-info-row">
          <span>Lat: {safeToFixed(selectedEvent?.location?.coordinates?.[1], 4)}° S</span>
          <span>Lng: {safeToFixed(selectedEvent?.location?.coordinates?.[0], 4)}° E</span>
        </div>
        <div className="map-info-row">
          <span>{selectedEvent?.satelliteData?.imagerySource || 'Sentinel-2 L2A'}</span>
          <span>Selected: {selectedEvent?.erfNumber || 'None'}</span>
        </div>
        <div className="map-info-row">
          <span>Last Updated: {new Date().toLocaleString()}</span>
        </div>
        {selectedEvent?.satelliteData?.ndviChange !== undefined && (
          <div className="change-detection">
            <span className="change-label">Change Detection</span>
            <span className={`change-value ${selectedEvent.satelliteData.ndviChange < 0 ? 'negative' : 'positive'}`}>
              {safeToFixed(selectedEvent.satelliteData.ndviChange * 100, 1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SatelliteMap;
